import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, Validators } from '@angular/forms';
import { ApiService } from 'src/app/core/services/api.service';
import { PaginationService } from 'src/app/core/services/pagination.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OtpVerificationModalComponent } from 'src/app/shared/components/otp-verification-modal/otp-verification-modal.component';
import Swal from 'sweetalert2';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss'],
  standalone: false
})
export class PurchaseOrdersComponent implements OnInit {

  // Breadcrumb
  breadCrumbItems!: Array<{}>;
  
  // Form
  purchaseOrderForm!: UntypedFormGroup;
  submitted = false;
  isEditMode = false;
  editId: any = null;
  showForm = false; // Toggle form visibility
  
  // Data
  purchaseOrders: any[] = [];
  allPurchaseOrders: any[] = [];
  suppliers: any[] = [];
  itemsList: any[] = [];
  demands: any[] = [];
  selectedDemand: any = null;
  isLoading = false;
  isDownloading = false;
  
  // Pagination
  page: number = 1;
  pageSize: number = 10;
  
  // Filters
  searchTerm: string = '';
  statusFilter: string = '';
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    public paginationService: PaginationService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    // Breadcrumb
    this.breadCrumbItems = [
      { label: 'Stock Management' },
      { label: 'Purchase Orders', active: true }
    ];

    // Initialize Form
    this.initializeForm();

    // Load data
    this.loadSuppliers();
    this.loadDemands();
    this.loadPurchaseOrders();
  }

  /**
   * Initialize Form
   */
  initializeForm(): void {
    this.purchaseOrderForm = this.formBuilder.group({
      demand_id: ['', [Validators.required]],
      terms_and_conditions: [''],
      remarks: ['']
    });

    // Listen to demand changes
    this.purchaseOrderForm.get('demand_id')?.valueChanges.subscribe(demandId => {
      if (demandId) {
        this.onDemandChange(demandId);
      } else {
        this.selectedDemand = null;
      }
    });
  }

  /**
   * Get form controls
   */
  get f() {
    return this.purchaseOrderForm.controls;
  }


  /**
   * Load Purchase Orders
   */
  loadPurchaseOrders(): void {
    this.isLoading = true;
    
    this.apiService.get('/purchase_orders').subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        if (response.success) {
          this.allPurchaseOrders = response.data || [];
          this.purchaseOrders = cloneDeep(this.allPurchaseOrders);
          this.purchaseOrders = this.paginationService.changePage(this.allPurchaseOrders);
        } else {
          this.allPurchaseOrders = [];
          this.purchaseOrders = [];
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading purchase orders:', error);
        Swal.fire('Error', 'Failed to load purchase orders', 'error');
      }
    });
  }

  /**
   * Load Suppliers
   */
  loadSuppliers(): void {
    this.apiService.get('/suppliers').subscribe({
      next: (response: any) => {
        if (response.success) {
          this.suppliers = response.data || [];
        } else {
          this.suppliers = [];
        }
      },
      error: (error) => {
        console.error('Error loading suppliers:', error);
        this.suppliers = [];
      }
    });
  }

  /**
   * Load Items
   */
  loadItems(supplierId?: number): void {
    this.apiService.get(`/items?supplier_id=${supplierId}`).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.itemsList = response.data || [];
        } else {
          this.itemsList = [];
        }
      },
      error: (error) => {
        console.error('Error loading items:', error);
        this.itemsList = [];
      }
    });
  }

  /**
   * Load Demands
   */
  loadDemands(): void {
    this.apiService.get('/demands?status=Pending').subscribe({
      next: (response: any) => {
        if (response.success) {
          this.demands = response.data || [];
        } else {
          this.demands = [];
        }
      },
      error: (error) => {
        console.error('Error loading demands:', error);
        this.demands = [];
      }
    });
  }

  /**
   * Handle demand change
   */
  onDemandChange(demandId: number): void {
    this.selectedDemand = this.demands.find(d => d.id == demandId);
    
    if (!this.selectedDemand) {
      return;
    }
  }

  /**
   * Calculate subtotal from demand items
   */
  calculateSubtotal(demand: any): number {
    if (!demand || !demand.items || demand.items.length === 0) {
      return 0;
    }

    let subtotal = 0;
    demand.items.forEach((item: any) => {
      const quantity = parseFloat(item.requestedQuantity || 0);
      const price = parseFloat(item.price_history?.price || 0);
      subtotal += quantity * price;
    });

    return subtotal;
  }

  /**
   * Calculate tax amount from demand items
   */
  calculateTaxAmount(demand: any): number {
    if (!demand || !demand.items || demand.items.length === 0) {
      return 0;
    }

    let taxAmount = 0;
    demand.items.forEach((item: any) => {
      const quantity = parseFloat(item.requestedQuantity || 0);
      const price = parseFloat(item.price_history?.price || 0);
      const gstRate = parseFloat(item.item?.gstRate || 0);
      const itemSubtotal = quantity * price;
      taxAmount += (itemSubtotal * gstRate) / 100;
    });

    return taxAmount;
  }

  /**
   * Calculate grand total from demand items
   */
  calculateGrandTotal(demand: any): number {
    const subtotal = this.calculateSubtotal(demand);
    const taxAmount = this.calculateTaxAmount(demand);
    return subtotal + taxAmount;
  }

  /**
   * Toggle form visibility
   */
  toggleForm(): void {
    this.showForm = !this.showForm;
    if (this.showForm) {
      this.isEditMode = false;
      this.editId = null;
      this.submitted = false;
      this.resetForm();
    }
  }

  /**
   * Reset form
   */
  resetForm(): void {
    this.selectedDemand = null;
    this.purchaseOrderForm.reset({
      demand_id: '',
      terms_and_conditions: '',
      remarks: ''
    });
  }

  /**
   * Check if purchase order can be edited
   */
  canEditPurchaseOrder(order: any): boolean {
    return order.status === 'Draft' || order.status === 'Pending';
  }


  /**
   * Save Purchase Order
   */
  savePurchaseOrder(): void {
    this.submitted = true;

    if (this.purchaseOrderForm.invalid) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }

    // Open OTP verification modal
    const modalRef = this.modalService.open(OtpVerificationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    
    modalRef.componentInstance.transactionType = 'purchase_order';
    modalRef.componentInstance.operation = this.isEditMode ? 'update' : 'create';
    
    modalRef.result.then(
      (result) => {
        if (result?.verified) {
          // OTP verified, proceed with save
          this.proceedWithSave(result.verificationHash);
        }
      },
      () => {
        // Modal dismissed/cancelled
      }
    );
  }

  /**
   * Proceed with save after OTP verification
   */
  private proceedWithSave(verificationHash: string): void {
    const demandId = this.purchaseOrderForm.get('demand_id')?.value;
    
    // Create PO from demand
    const formData = {
      demand_id: demandId,
      verification_hash: verificationHash,
      terms_and_conditions: this.purchaseOrderForm.get('terms_and_conditions')?.value || '',
      remarks: this.purchaseOrderForm.get('remarks')?.value || ''
    };

    this.apiService.post('/purchase_orders/from_demand', formData).subscribe({
      next: (response: any) => {
        if (response.success) {
          Swal.fire('Success', 'Purchase order created from demand successfully', 'success');
          this.loadPurchaseOrders();
          this.toggleForm();
        } else {
          Swal.fire('Error', response.message || 'Failed to create purchase order from demand', 'error');
        }
      },
      error: (error) => {
        Swal.fire('Error', error.error?.message || 'Failed to create purchase order from demand', 'error');
      }
    });
  }

  /**
   * Cancel form
   */
  cancelForm(): void {
    this.toggleForm();
  }

  /**
   * Delete Purchase Order
   */
  deletePurchaseOrder(id: any): void {
    // Find the order to check its status
    const order = this.purchaseOrders.find((po: any) => (po.id || po._id) === id);
    
    // Check if order can be deleted
    if (order && !this.canEditPurchaseOrder(order)) {
      Swal.fire('Error', 'Only Draft or Pending purchase orders can be deleted', 'error');
      return;
    }
    
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        // Open OTP verification modal
        const modalRef = this.modalService.open(OtpVerificationModalComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false
        });
        
        modalRef.componentInstance.transactionType = 'purchase_order';
        modalRef.componentInstance.operation = 'delete';
        
        modalRef.result.then(
          (otpResult) => {
            if (otpResult?.verified) {
              // OTP verified, proceed with delete
              this.proceedWithDelete(id, otpResult.verificationHash);
            }
          },
          () => {
            // Modal dismissed/cancelled
          }
        );
      }
    });
  }

  /**
   * Proceed with delete after OTP verification
   */
  private proceedWithDelete(id: any, verificationHash: string): void {
    const payload = { verification_hash: verificationHash };
    this.apiService.delete(`/purchase_orders/${id}`).subscribe({
      next: (response: any) => {
        if (response.success) {
          Swal.fire('Deleted!', 'Purchase order has been deleted.', 'success');
          this.loadPurchaseOrders();
        } else {
          Swal.fire('Error', response.message || 'Failed to delete purchase order', 'error');
        }
      },
      error: (error) => {
        Swal.fire('Error', error.message || 'Failed to delete purchase order', 'error');
      }
    });
  }

  /**
   * View Purchase Order Details
   */
  viewPurchaseOrder(order: any): void {
    let itemsHtml = '';
    
    if (order.items && order.items.length > 0) {
      itemsHtml = '<div class="table-responsive mt-3"><table class="table table-sm table-bordered">';
      itemsHtml += '<thead class="table-light"><tr><th>#</th><th>Item</th><th>Quantity</th><th>Unit</th><th>Unit Price</th><th>Tax Rate</th><th>Total</th></tr></thead><tbody>';
      
      order.items.forEach((item: any, index: number) => {
        itemsHtml += `<tr>
          <td>${index + 1}</td>
          <td>${item.item?.name || '-'}</td>
          <td>${item.quantity || '-'}</td>
          <td>${item.item?.unit?.symbol || '-'}</td>
          <td>₹${item.unitPrice ? parseFloat(item.unitPrice).toFixed(2) : '0.00'}</td>
          <td>${item.taxRate || 0}%</td>
          <td>₹${item.totalPrice ? parseFloat(item.totalPrice).toFixed(2) : '0.00'}</td>
        </tr>`;
      });
      
      itemsHtml += '</tbody></table></div>';
    } else {
      itemsHtml = '<p class="text-muted">No items found</p>';
    }

    Swal.fire({
      title: `<strong>Purchase Order Details</strong>`,
      html: `
        <div class="text-start">
          <div class="row mb-2">
            <div class="col-6"><strong>PO Number:</strong></div>
            <div class="col-6">${order.poNumber || '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Supplier:</strong></div>
            <div class="col-6">${order.supplier?.name || '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>PO Date:</strong></div>
            <div class="col-6">${order.poDate ? new Date(order.poDate).toLocaleDateString() : '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Status:</strong></div>
            <div class="col-6"><span class="badge ${this.getStatusClass(order.status)}">${order.status || '-'}</span></div>
          </div>
          ${order.demand ? `<div class="row mb-2">
            <div class="col-6"><strong>Related Demand:</strong></div>
            <div class="col-6">${order.demand.demandNumber || '-'}</div>
          </div>` : ''}
          ${order.termsAndConditions ? `<div class="row mb-2">
            <div class="col-6"><strong>Terms & Conditions:</strong></div>
            <div class="col-6">${order.termsAndConditions}</div>
          </div>` : ''}
          ${order.remarks ? `<div class="row mb-2">
            <div class="col-6"><strong>Remarks:</strong></div>
            <div class="col-6">${order.remarks}</div>
          </div>` : ''}
          <hr>
          <h6 class="mt-3 mb-2">Items</h6>
          ${itemsHtml}
          <hr>
          <div class="row mb-2">
            <div class="col-6"><strong>Subtotal:</strong></div>
            <div class="col-6">₹${order.totalAmount ? parseFloat(order.totalAmount).toFixed(2) : '0.00'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Tax Amount:</strong></div>
            <div class="col-6">₹${order.taxAmount ? parseFloat(order.taxAmount).toFixed(2) : '0.00'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Grand Total:</strong></div>
            <div class="col-6"><strong>₹${order.grandTotal ? parseFloat(order.grandTotal).toFixed(2) : '0.00'}</strong></div>
          </div>
        </div>
      `,
      width: '800px',
      showCloseButton: true,
      confirmButtonText: 'Close'
    });
  }

  /**
   * Cancel Purchase Order
   */
  cancelPurchaseOrder(id: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to cancel this purchase order?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f06548',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, cancel it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.apiService.post(`/purchase_orders/${id}/cancel`, {}).subscribe({
          next: (response: any) => {
            if (response.success) {
              Swal.fire('Cancelled!', 'Purchase order has been cancelled.', 'success');
              this.loadPurchaseOrders();
            } else {
              Swal.fire('Error', response.message || 'Failed to cancel purchase order', 'error');
            }
          },
          error: (error) => {
            Swal.fire('Error', error.message || 'Failed to cancel purchase order', 'error');
          }
        });
      }
    });
  }

  /**
   * Search
   */
  performSearch(): void {
    const term = (this.searchTerm || '').toLowerCase().trim();
    const source: any[] = Array.isArray(this.allPurchaseOrders) ? this.allPurchaseOrders : [];

    if (term) {
      this.purchaseOrders = source.filter((order: any) => {
        const po = (order.po_number || order.poNumber || '').toString().toLowerCase();
        const status = (order.status || '').toString().toLowerCase();
        const supplierId = (order.supplier_id ?? order.supplierId ?? '').toString();
        const supplierName = ((order.supplier && order.supplier.name) || '').toString().toLowerCase();
        return po.includes(term) || status.includes(term) || supplierId.includes(term) || supplierName.includes(term);
      });
    } else {
      this.purchaseOrders = cloneDeep(source);
    }
    this.purchaseOrders = this.paginationService.changePage(this.purchaseOrders);
  }

  /**
   * Filter by status
   */
  filterByStatus(): void {
    const source: any[] = Array.isArray(this.allPurchaseOrders) ? this.allPurchaseOrders : [];
    const status = (this.statusFilter || '').toString();

    if (status) {
      this.purchaseOrders = source.filter((order: any) => (order.status || '') === status);
    } else {
      this.purchaseOrders = cloneDeep(source);
    }
    this.purchaseOrders = this.paginationService.changePage(this.purchaseOrders);
  }

  /**
   * Change page
   */
  changePage(): void {
    const source: any[] = Array.isArray(this.allPurchaseOrders) ? this.allPurchaseOrders : [];
    this.purchaseOrders = this.paginationService.changePage(source);
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string): string {
    const statusClasses: any = {
      'Draft': 'bg-secondary-subtle text-secondary',
      'Pending': 'bg-warning-subtle text-warning',
      'Approved': 'bg-info-subtle text-info',
      'Received': 'bg-success-subtle text-success',
      'Cancelled': 'bg-danger-subtle text-danger'
    };
    return statusClasses[status] || 'bg-secondary-subtle text-secondary';
  }

  /**
   * Download Purchase Order PDF
   */
  downloadPurchaseOrder(id: any): void {
    if (!id) {
      Swal.fire('Error', 'Invalid purchase order ID', 'error');
      return;
    }

    // Set loading state
    this.isDownloading = true;

    // First, get the download URL from the API
    this.apiService.get(`/purchase_orders/${id}/download_pdf`).subscribe({
      next: (response: any) => {
        if (response.success && response.data?.url) {
          // Extract the endpoint from the full URL
          const fileUrl = response.data.url;
          
          // Download the file using the URL
          this.apiService.downloadFile(fileUrl).subscribe({
            next: (blob: Blob) => {
              // Create a blob URL and trigger download
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `purchase_order_${id}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              
              this.isDownloading = false;
              Swal.fire('Success', 'Purchase order PDF downloaded successfully', 'success');
            },
            error: (error) => {
              console.error('Error downloading file:', error);
              this.isDownloading = false;
              Swal.fire('Error', 'Failed to download purchase order PDF', 'error');
            }
          });
        } else {
          this.isDownloading = false;
          Swal.fire('Error', 'Download URL not found in response', 'error');
        }
      },
      error: (error) => {
        console.error('Error downloading purchase order:', error);
        this.isDownloading = false;
        Swal.fire('Error', 'Failed to download purchase order PDF', 'error');
      }
    });
  }

  /**
   * Send Email to Supplier
   */
  sendEmailToSupplier(id: any): void {
    if (!id) {
      Swal.fire('Error', 'Invalid purchase order ID', 'error');
      return;
    }

    Swal.fire({
      title: 'Send Email?',
      text: 'Do you want to send this purchase order to the supplier via email?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, send it!'
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Sending Email...',
          text: 'Please wait',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        this.apiService.post(`/purchase_orders/${id}/send_email`, {}).subscribe({
          next: (response: any) => {
            if (response.success) {
              Swal.fire('Success!', 'Email sent successfully to the supplier', 'success');
            } else {
              Swal.fire('Error', response.message || 'Failed to send email', 'error');
            }
          },
          error: (error) => {
            Swal.fire('Error', error.message || 'Failed to send email to supplier', 'error');
          }
        });
      }
    });
  }

 
}
